// 非編集表示・コメント本文が共有する、Markdown の表（GFM 基本形）の描画。
//
// - レイアウトだけを持ち、リンクの挙動を固定しない。セルの中身の描画は呼び出し側が渡す
//   レンダラーに委ねる（キャンバスオブジェクトは StyledLinkifiedText の遅延オープン・
//   ダブルクリック譲り、コメント本文は LinkifiedCommentText の標準アンカー + stopPropagation）。
// - 列幅は共有の純粋関数（markdownTableViewModel → markdownTableColumnWidthsEm）が決めた em 値を
//   `colgroup` へ与え、表自身の `width` も合計値で明示する（`100%` にすると余剰幅が再配分され、
//   `<col>` の指定と実描画幅がずれて Canvas 出力と食い違う）。
//   保存済み列幅（savedWidths）がある表は、同じ共有関数が列数とヘッダ指紋の一致を確かめてから採用する。
// - セル要素（th / td）だけが `data-md-line`（raw 行 index）と `data-md-cell`（列 index）を持つ。
//   wrapper・table・tr には付けない。付けると、cellIndex を持たないセレクタ
//   `[data-md-line="i"]` が wrapper を選び、リモートキャレット・選択帯の Range が表全体を
//   基準にしてしまう。
// - 計測ビルダー（buildMarkdownTextContent の appendMeasuredTable）と同じ構造を作る
//   （構造一致はパリティテストで担保）。列幅つまみは絶対配置で行送り・セル幅に影響しないため、
//   つまみを持たない計測ビルダーとの計測結果は一致する。
// - 列幅つまみ（onResizeColumns を渡したときだけ描く）のドラッグ状態は、この表だけの
//   ローカル state に閉じる。他参加者へ共有する必要が無く、オブジェクトをまたいで残ると
//   後始末の責任が曖昧になるためである。本文編集で表が消えた・並びが変わった場合は、
//   呼び出し側の key（ヘッダ行の raw 行 index）が変わって再マウントされ、状態は破棄される。

import {
  useCallback,
  useEffect,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import type {
  MarkdownTable,
  MarkdownTableRow,
  TableColumnWidths,
  TextInlineDisplaySpan,
  TextInlineStyleRange,
} from "@refinement-canvas/domain";
import {
  MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_COLUMNS,
  MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_TABLES,
  MARKDOWN_TABLE_COLUMN_WIDTH_MAX_EM,
  TABLE_MIN_COLUMN_EM,
} from "@refinement-canvas/domain";
import {
  markdownTableCellSpans,
  markdownTableViewModel,
  MARKDOWN_TABLE_CLASS,
  MARKDOWN_TABLE_RESIZER_CLASS,
  MARKDOWN_TABLE_WRAP_CLASS,
  type MarkdownTableViewModel,
} from "../../../lib/markdown-text";
import type {
  MarkdownTableBodyCellContext,
  MarkdownTableBodyPalette,
} from "../../../lib/text-link-color";
import { useCanvasStore } from "../store/canvas-store";

/** ヘッダーと本文を混同せず、本文だけへ行面の色補正情報を渡す判別共用体。 */
export type MarkdownTableCellContext =
  | { readonly kind: "header" }
  | ({ readonly kind: "body" } & MarkdownTableBodyCellContext);

/** 列幅つまみのドラッグ中だけ持つ一時状態（この表のローカル state）。 */
interface ColumnDragState {
  /** ドラッグを始めた時点の表のブロック順 index。描画中に変わったら状態を破棄する。 */
  readonly tableIndex: number;
  /**
   * ドラッグを始めた時点のヘッダ指紋。列数とブロック順 index が変わらないまま本文の
   * ヘッダ行だけが書き換わることがあるため（この表のコンポーネント key はヘッダ行の
   * raw 行 index で、そのときも変わらない）、指紋も照合して状態を破棄する。
   */
  readonly headerKey: string | undefined;
  /** 掴んだ列境界の左側の列 index。 */
  readonly boundaryIndex: number;
  readonly pointerId: number;
  readonly startClientX: number;
  /** 回転した図形でローカル横軸へ射影するために、縦の移動量も要る。 */
  readonly startClientY: number;
  readonly startWidthsEm: readonly number[];
  readonly currentWidthsEm: readonly number[];
}

interface Props {
  /** 生の Markdown 本文全体（セルの raw 範囲は本文全体基準のため）。 */
  readonly text: string;
  readonly table: MarkdownTable;
  /** 表の使用可能幅（em）。呼び出し側が本文領域の幅と基準フォント px から求める。 */
  readonly availableEm: number;
  /** 文字範囲だけへ適用する部分書式（正規形の run 配列）。 */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /** 表本文の基底文字色。 */
  readonly bodyColor: string;
  /** 図形の塗りと本文色から解決した、奇数・偶数行の不透明な配色。 */
  readonly bodyPalette: MarkdownTableBodyPalette;
  /** セルの中身の描画。リンクの挙動は呼び出し側が決める。 */
  readonly renderCell: (
    spans: readonly TextInlineDisplaySpan[],
    context: MarkdownTableCellContext
  ) => ReactNode;
  /** 本文中の表のブロック順 index（保存できる上限の判定に使う）。 */
  readonly tableIndex?: number | undefined;
  /** この表の保存済み列幅。未指定・`null` は自動列幅。 */
  readonly savedWidths?: TableColumnWidths | null | undefined;
  /**
   * 列幅の確定。未指定のときはつまみを描かない（表示のみ）。
   * 空配列は「保存を破棄して自動列幅へ戻す」を表す。
   */
  readonly onResizeColumns?:
    | ((widthsEm: readonly number[], headerKey: string) => void)
    | undefined;
  /** 図形の回転角（度）。ドラッグ量を図形ローカル横軸へ射影するために必要。 */
  readonly rotationDeg?: number | undefined;
  /** 本文の基準フォントサイズ（px）。ドラッグ量を em へ換算するために必要。 */
  readonly baseFontPx?: number | undefined;
  /** 解析した表のヘッダ指紋。保存時にそのまま送る。 */
  readonly headerKey?: string | undefined;
}

export function MarkdownTableView({
  text,
  table,
  availableEm,
  inlineStyles,
  bodyColor,
  bodyPalette,
  renderCell,
  tableIndex,
  savedWidths,
  onResizeColumns,
  rotationDeg,
  baseFontPx,
  headerKey,
}: Props): JSX.Element {
  const [drag, setDrag] = useState<ColumnDragState | null>(null);
  const model = markdownTableViewModel(text, table, availableEm, {
    savedWidths,
    ...(drag === null ? {} : { previewWidthsEm: drag.currentWidthsEm }),
  });
  // 表の列幅つまみは「調整できる状態」のときだけ描く。参照専用・ロック中・編集中・コメント本文では
  // 呼び出し側が onResizeColumns を渡さないため、ここでの判定は存在確認と保存上限だけでよい。
  const resizable =
    onResizeColumns !== undefined &&
    tableIndex !== undefined &&
    headerKey !== undefined &&
    baseFontPx !== undefined &&
    baseFontPx > 0 &&
    isResizableTable(tableIndex, model.columnWidthsEm);

  // 後始末の一本化。pointerup 以外の終了契機（pointercancel・capture 喪失・アンマウント・
  // 調整できない状態への遷移・列数の変化）から共通で呼ぶ。
  const endDrag = useCallback(() => {
    setDrag(null);
  }, []);

  // 「調整できない状態へ遷移した」「本文編集で列数・表の位置・ヘッダ行が変わった」ときは、
  // つまみが消えるか対象がずれるため、進行中のドラッグ状態を残さない
  // （残すと次の描画で古い幅を当て続け、離した時点で別の表へ古い幅を保存してしまう）。
  const dragTableIndex = drag?.tableIndex;
  const dragColumnCount = drag?.startWidthsEm.length;
  const dragHeaderKey = drag?.headerKey;
  useEffect(() => {
    if (dragTableIndex === undefined) return;
    if (
      !resizable ||
      dragTableIndex !== tableIndex ||
      dragColumnCount !== table.columnCount ||
      dragHeaderKey !== headerKey
    ) {
      endDrag();
    }
  }, [
    dragColumnCount,
    dragHeaderKey,
    dragTableIndex,
    endDrag,
    headerKey,
    resizable,
    table.columnCount,
    tableIndex,
  ]);

  // ドラッグ中のアンマウント（本文編集で表が消える等）では、この state がコンポーネントごと
  // 破棄され、つまみの要素が DOM から外れることで Pointer Capture も暗黙に解放される。
  // そのため cleanup で明示的に行う後始末は無く、専用の effect も置かない。

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLSpanElement>, boundaryIndex: number): void => {
      if (!resizable || event.button !== 0) return;
      // 親（テキスト図形・付箋）と stage の pointerdown ハンドラを起こさない。図形の選択・移動は
      // use-object-drag.ts 側でも見送るが、その他のハンドラへも伝えない。
      event.stopPropagation();
      // 進行中のドラッグがあるあいだは、別のポインタでの開始を受け付けない。
      // 受け付けると、先のポインタの Capture を解放しないまま state を上書きしてしまう。
      if (drag !== null) return;
      // jsdom など Pointer Capture を持たない環境でも、ドラッグ自体は成立させる。
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDrag({
        boundaryIndex,
        currentWidthsEm: model.columnWidthsEm,
        headerKey,
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startWidthsEm: model.columnWidthsEm,
        tableIndex,
      });
    },
    [drag, headerKey, model.columnWidthsEm, resizable, tableIndex]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLSpanElement>): void => {
      if (drag === null || drag.pointerId !== event.pointerId || baseFontPx === undefined) return;
      event.stopPropagation();
      const zoom = useCanvasStore.getState().view.zoom;
      const nextWidths = resizeColumnWidths({
        boundaryIndex: drag.boundaryIndex,
        deltaEm: localDeltaEm({
          baseFontPx,
          dx: event.clientX - drag.startClientX,
          dy: event.clientY - drag.startClientY,
          rotationDeg,
          zoom,
        }),
        startWidthsEm: drag.startWidthsEm,
      });
      // 最小列幅で止まっている間は同じ幅が返り続ける。再描画を増やさないよう、変化したときだけ更新する。
      if (isSameColumnWidths(nextWidths, drag.currentWidthsEm)) return;
      setDrag({ ...drag, currentWidthsEm: nextWidths });
    },
    [baseFontPx, drag, rotationDeg]
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent<HTMLSpanElement>): void => {
      if (drag === null || drag.pointerId !== event.pointerId) return;
      event.stopPropagation();
      const widthsEm = drag.currentWidthsEm;
      const moved = !isSameColumnWidths(widthsEm, drag.startWidthsEm);
      releaseCapture(event);
      endDrag();
      if (onResizeColumns === undefined || headerKey === undefined) return;
      if (!canCommitColumnWidths({ dragHeaderKey: drag.headerKey, headerKey, moved })) return;
      onResizeColumns(widthsEm, headerKey);
    },
    [drag, endDrag, headerKey, onResizeColumns]
  );

  const handlePointerAbort = useCallback(
    (event: PointerEvent<HTMLSpanElement>): void => {
      if (drag === null || drag.pointerId !== event.pointerId) return;
      // op を送らずに破棄する（幅は保存値・自動列幅へ戻る）。
      releaseCapture(event);
      endDrag();
    },
    [drag, endDrag]
  );

  const handleDoubleClick = useCallback(
    (event: MouseEvent<HTMLSpanElement>): void => {
      // 親（テキスト図形・付箋）は onDoubleClick で編集モードへ入る。伝えると「自動列幅へ戻す」と
      // 同時に本文が生の行へ変わり、操作結果が見えなくなる。
      event.stopPropagation();
      event.preventDefault();
      if (!resizable || onResizeColumns === undefined) return;
      // 空配列は「保存を破棄して自動列幅へ戻す」。ヘッダ指紋は送信側で空文字列へ落とされる。
      onResizeColumns([], "");
    },
    [onResizeColumns, resizable]
  );

  return (
    <div className={MARKDOWN_TABLE_WRAP_CLASS}>
      <table className={MARKDOWN_TABLE_CLASS} style={{ width: `${model.totalWidthEm}em` }}>
        <colgroup>
          {model.columnWidthsEm.map((widthEm, index) => (
            // 列は静的な並びで入れ替わらないため index key で問題ない。
            <col key={index} style={{ width: `${widthEm}em` }} />
          ))}
        </colgroup>
        <thead>
          <MarkdownTableRowView
            cellTag="th"
            bodyColor={bodyColor}
            bodyPalette={bodyPalette}
            inlineStyles={inlineStyles}
            model={model}
            renderCell={renderCell}
            row={model.header}
            text={text}
            {...(resizable
              ? {
                  resizer: {
                    draggingBoundaryIndex: drag?.boundaryIndex,
                    onDoubleClick: handleDoubleClick,
                    onPointerAbort: handlePointerAbort,
                    onPointerDown: handlePointerDown,
                    onPointerMove: handlePointerMove,
                    onPointerUp: handlePointerUp,
                  },
                }
              : {})}
          />
        </thead>
        {model.body.length > 0 ? (
          <tbody>
            {model.body.map((row, bodyRowIndex) => (
              <MarkdownTableRowView
                key={row.lineIndex}
                cellTag="td"
                bodyColor={bodyColor}
                bodyPalette={bodyPalette}
                bodyRowIndex={bodyRowIndex}
                inlineStyles={inlineStyles}
                model={model}
                renderCell={renderCell}
                row={row}
                text={text}
              />
            ))}
          </tbody>
        ) : null}
      </table>
    </div>
  );
}

/** ヘッダ行のセルへ渡す、列幅つまみの描画とイベント。 */
interface ColumnResizerProps {
  readonly draggingBoundaryIndex?: number | undefined;
  readonly onPointerDown: (event: PointerEvent<HTMLSpanElement>, boundaryIndex: number) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLSpanElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLSpanElement>) => void;
  readonly onPointerAbort: (event: PointerEvent<HTMLSpanElement>) => void;
  readonly onDoubleClick: (event: MouseEvent<HTMLSpanElement>) => void;
}

function MarkdownTableRowView({
  text,
  model,
  row,
  cellTag,
  bodyColor,
  bodyPalette,
  bodyRowIndex,
  inlineStyles,
  renderCell,
  resizer,
}: {
  readonly text: string;
  readonly model: MarkdownTableViewModel;
  readonly row: MarkdownTableRow;
  readonly cellTag: "th" | "td";
  readonly bodyColor: string;
  readonly bodyPalette: MarkdownTableBodyPalette;
  readonly bodyRowIndex?: number | undefined;
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  readonly renderCell: (
    spans: readonly TextInlineDisplaySpan[],
    context: MarkdownTableCellContext
  ) => ReactNode;
  readonly resizer?: ColumnResizerProps | undefined;
}): JSX.Element {
  const CellTag = cellTag;
  const cellContext: MarkdownTableCellContext =
    cellTag === "th" || bodyRowIndex === undefined
      ? { kind: "header" }
      : {
          kind: "body",
          bodyColor,
          bodyRowIndex,
          background:
            (bodyRowIndex + 1) % 2 === 0
              ? bodyPalette.stripeBackground
              : bodyPalette.bodyBackground,
          palette: bodyPalette,
        };
  // 最終列にはつまみを置かない（表全体の幅は図形幅で決まるため、右端は動かせない）。
  const lastBoundaryIndex = model.columnWidthsEm.length - 2;
  return (
    <tr>
      {row.cells.map((cell, cellIndex) => (
        // セルは列と 1:1 の静的な並びのため index key で問題ない。
        <CellTag
          key={cellIndex}
          data-md-line={row.lineIndex}
          data-md-cell={cellIndex}
          // 列ヘッダーは支援技術へ「列の見出し」として伝える（GFM の表はヘッダ行が列見出し）。
          scope={cellTag === "th" ? "col" : undefined}
          // 寄せは区切り行の `:` に従う。左寄せも明示し、コンテナの中央 / 右寄せを継承させない。
          style={{ textAlign: model.aligns[cellIndex] ?? "left" }}
        >
          {renderCell(markdownTableCellSpans(text, cell, inlineStyles), cellContext)}
          {resizer !== undefined && cellIndex <= lastBoundaryIndex ? (
            <span
              aria-hidden="true"
              className={MARKDOWN_TABLE_RESIZER_CLASS}
              data-canvas-table-resize="true"
              data-column-boundary={cellIndex}
              data-dragging={resizer.draggingBoundaryIndex === cellIndex ? "true" : undefined}
              onDoubleClick={resizer.onDoubleClick}
              onLostPointerCapture={resizer.onPointerAbort}
              onPointerCancel={resizer.onPointerAbort}
              onPointerDown={(event) => resizer.onPointerDown(event, cellIndex)}
              onPointerMove={resizer.onPointerMove}
              onPointerUp={resizer.onPointerUp}
            />
          ) : null}
        </CellTag>
      ))}
    </tr>
  );
}

/**
 * 保存上限を超える表・列にはつまみを描かない。保存できるのは表 32 件・列 32 件まで、
 * 1 列の幅は 512em までで、超えた対象で操作を許すと必ずサーバ拒否になる
 * （表示そのものは従来どおり自動列幅で行う）。
 * 列が 1 つしかない表も、動かせる列境界を持たないため対象にしない。
 */
function isResizableTable(tableIndex: number, columnWidthsEm: readonly number[]): boolean {
  if (tableIndex >= MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_TABLES) return false;
  if (columnWidthsEm.length > MARKDOWN_TABLE_COLUMN_WIDTHS_MAX_COLUMNS) return false;
  if (columnWidthsEm.length < 2) return false;
  // 1 列でも保存上限を超える幅で表示されている表は、どの列幅を送ってもサーバ拒否になる
  // （本文が極端に長いセルを持つ表を、とても広い図形へ置いた場合に起こり得る）。
  return columnWidthsEm.every((width) => width <= MARKDOWN_TABLE_COLUMN_WIDTH_MAX_EM);
}

/**
 * 画面上の移動量を、図形のローカル横軸へ射影して em へ換算する。
 * 図形は回転している場合があり（`object.rotation`）、画面 X の移動量をそのまま使うと
 * 列の横軸と一致せず、縦方向に動かしても幅が変わってしまう。
 */
export function localDeltaEm(params: {
  readonly dx: number;
  readonly dy: number;
  readonly rotationDeg?: number | undefined;
  readonly zoom: number;
  readonly baseFontPx: number;
}): number {
  const { baseFontPx, dx, dy, zoom } = params;
  if (!(baseFontPx > 0) || !(zoom > 0)) return 0;
  const theta = ((params.rotationDeg ?? 0) * Math.PI) / 180;
  // 画面ベクトルを -theta 回転して図形ローカル座標へ戻す。
  const localDx = dx * Math.cos(-theta) - dy * Math.sin(-theta);
  return localDx / zoom / baseFontPx;
}

/**
 * 列境界のドラッグ結果の列幅。左右 2 列の**合計を保つ**ことで表全体の幅を変えず、
 * どちらかが最小列幅に達したらそこで止める。あわせて、どちらの列も保存上限（512em）を
 * 超えないところで止める（超えた幅を確定しても、スキーマが拒否して保存されないため）。
 */
export function resizeColumnWidths(params: {
  readonly startWidthsEm: readonly number[];
  readonly boundaryIndex: number;
  readonly deltaEm: number;
}): readonly number[] {
  const { boundaryIndex, deltaEm, startWidthsEm } = params;
  const left = startWidthsEm[boundaryIndex];
  const right = startWidthsEm[boundaryIndex + 1];
  if (left === undefined || right === undefined) return startWidthsEm;
  const total = left + right;
  // 表示される列幅は共有関数（resolveMarkdownTableColumnWidths）が最小列幅を保つため、通常は
  // ここへ最小列幅未満の値は来ない。それでも 2 列ぶんの最小幅を確保できない入力では動かさない。
  if (total < TABLE_MIN_COLUMN_EM * 2) return startWidthsEm;
  // 下限は「左が最小」と「右が上限」、上限は「左が上限」と「右が最小」で決まる。
  const lowerEm = Math.max(TABLE_MIN_COLUMN_EM, total - MARKDOWN_TABLE_COLUMN_WIDTH_MAX_EM);
  const upperEm = Math.min(MARKDOWN_TABLE_COLUMN_WIDTH_MAX_EM, total - TABLE_MIN_COLUMN_EM);
  if (lowerEm > upperEm) return startWidthsEm;
  // 先に可動範囲へ収めてから 1/1000em の格子へ丸め、丸めが範囲の外側へ明確にはみ出した場合だけ
  // 1 格子ぶん内側へ戻す（丸め幅は 0.0005em 以下のため、1 格子で必ず範囲内に入る）。
  // 可動範囲そのものを格子へ丸めないのは、`Math.floor(value * 1000)` が格子上の値でも浮動小数の
  // 誤差で 1 格子ずれ得るためである（例: 5.453 × 1000 は 5452.99… になる）。同じ理由で、
  // はみ出しの判定にも浮動小数の誤差より十分大きく、格子幅より十分小さい許容差を置く。
  // これを怠ると、動かしていないのに幅が 0.001em 変わり、掴んで離すだけの操作が保存される。
  let nextLeft = roundEm(Math.min(upperEm, Math.max(lowerEm, left + deltaEm)));
  if (nextLeft < lowerEm - EM_BOUND_TOLERANCE) nextLeft = roundEm(nextLeft + EM_GRID);
  if (nextLeft > upperEm + EM_BOUND_TOLERANCE) nextLeft = roundEm(nextLeft - EM_GRID);
  // 幅が 1 つも変わらないなら、同じ配列を返して「動かしていない」ことを呼び出し側へ伝える
  // （ここで新しい配列を返すと、掴んだだけの操作が「確定」として保存されてしまう）。
  if (nextLeft === left) return startWidthsEm;
  const next = [...startWidthsEm];
  next[boundaryIndex] = nextLeft;
  next[boundaryIndex + 1] = roundEm(total - nextLeft);
  return next;
}

/**
 * 列幅の確定（op の送信）を行ってよいかを判定する。
 *
 * - `moved`: つまみを掴んで動かさずに離した場合（ダブルクリックの 1 打目を含む）は保存しない。
 *   送ると、自動列幅のままだった表が「いま見えている幅で確定した表」に変わってしまう。
 * - ヘッダ指紋: ドラッグ中に本文のヘッダ行だけが書き換わった場合、掴んだ表と確定先の表は別物になる。
 *   この場合の破棄は状態を捨てる effect も行うが、破棄より先に離した場合に備えて `pointerup` でも
 *   同じ条件を評価する。両者の条件が食い違わないよう、判定はこの純粋関数 1 か所だけに置く。
 */
export function canCommitColumnWidths(params: {
  readonly moved: boolean;
  readonly dragHeaderKey: string | undefined;
  readonly headerKey: string | undefined;
}): boolean {
  if (!params.moved) return false;
  if (params.headerKey === undefined) return false;
  return params.dragHeaderKey === params.headerKey;
}

/** 2 つの列幅配列が同じ内容かを判定する（無駄な再描画と、変化の無い保存を避けるために使う）。 */
function isSameColumnWidths(left: readonly number[], right: readonly number[]): boolean {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  return left.every((width, index) => width === right[index]);
}

/** 列幅を丸める格子の幅（em）。 */
const EM_GRID = 0.001;

/**
 * 可動範囲のはみ出し判定に使う許容差（em）。列幅の桁（最大 512em）で生じる浮動小数の誤差
 * （およそ 1e-13）より十分大きく、格子幅（0.001em）より十分小さい値にする。
 */
const EM_BOUND_TOLERANCE = 1e-9;

/** 列幅（em）を 1/1000 em へ丸める。浮動小数の誤差が CSS 値・保存値へ漏れないようにする。 */
function roundEm(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * つまみが握っている Pointer Capture を解放する。`pointerup` / `pointercancel` では仕様上
 * 暗黙に解放されるが、`lostpointercapture` 以外の経路でも確実に握りっぱなしを避けるため明示する。
 */
function releaseCapture(event: PointerEvent<HTMLSpanElement>): void {
  const target = event.currentTarget;
  if (target.hasPointerCapture?.(event.pointerId) !== true) return;
  target.releasePointerCapture?.(event.pointerId);
}
